import { Trash2 } from 'lucide-react';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  ListGroup,
  Modal,
  Spinner,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import api from '../../service/api';
import type { Effect, User } from '../../service/api/types';

interface ManagerUserEffectsModalProps {
  show: boolean;
  onHide: () => void;
  user: User | null;
}

const ManagerUserEffectsModal: React.FC<ManagerUserEffectsModalProps> = ({
  show,
  onHide,
  user,
}) => {
  const { t } = useTranslation();
  const [effects, setEffects] = useState<Effect[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const getEffectLabel = useCallback(
    (effect: Effect): string => {
      switch (effect.effectType) {
        case 'set_price':
          return t('effects.setPriceTo', { value: effect.value });
        case 'adjust_all_prices_percent':
          return t('effects.allPricesPercent', {
            value: `${effect.value > 0 ? '+' : ''}${effect.value}`,
          });
        case 'adjust_item_price_percent':
          return t('effects.itemPricePercent', {
            value: `${effect.value > 0 ? '+' : ''}${effect.value}`,
          });
        default:
          return t('effects.unknownEffect');
      }
    },
    [t],
  );

  const formatExpiry = useCallback(
    (expiresAt: DateTime | string): string => {
      const dt =
        typeof expiresAt === 'string' ? DateTime.fromISO(expiresAt) : expiresAt;
      if (!dt.isValid) return t('effects.unknown');
      const diff = dt.diffNow(['hours', 'minutes']);
      if (diff.hours < 1) {
        return t('effects.minutesRemaining', {
          count: Math.max(1, Math.round(diff.minutes)),
        });
      }
      return t('effects.hoursMinutesRemaining', {
        hours: Math.round(diff.hours),
        minutes: Math.round(diff.minutes % 60),
      });
    },
    [t],
  );

  const fetchEffects = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.effectsGetUserEffects(user._id);
      setEffects(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('effects.failedToLoadEffects'),
      );
    } finally {
      setIsLoading(false);
    }
  }, [user, t]);

  useEffect(() => {
    if (show && user) {
      fetchEffects();
    }
    if (!show) {
      setEffects([]);
      setError(null);
    }
  }, [show, user, fetchEffects]);

  const handleCancel = async (effectId: string) => {
    setCancellingId(effectId);
    try {
      await api.effectsCancel(effectId);
      setEffects(prev => prev.filter(e => e._id !== effectId));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('effects.failedToCancelEffect'),
      );
    } finally {
      setCancellingId(null);
    }
  };

  const userName = user ? `${user.given_name} ${user.family_name}` : '';

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {t('effects.activeEffects', { name: userName })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {!isLoading && !error && effects.length === 0 && (
          <p className="text-muted text-center mb-0">
            {t('effects.noActiveEffects')}
          </p>
        )}

        {!isLoading && effects.length > 0 && (
          <ListGroup variant="flush">
            {effects.map(effect => (
              <ListGroup.Item
                key={effect._id}
                className="d-flex justify-content-between align-items-center"
              >
                <div>
                  <div className="fw-semibold">{getEffectLabel(effect)}</div>
                  {effect.targetItem && (
                    <small className="text-muted">
                      {t('effects.target')}:{' '}
                      {typeof effect.targetItem === 'string'
                        ? effect.targetItem
                        : effect.targetItem.title}
                    </small>
                  )}
                  <div>
                    <Badge bg="secondary" className="mt-1">
                      {formatExpiry(effect.expiresAt)}
                    </Badge>
                  </div>
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleCancel(effect._id)}
                  disabled={cancellingId === effect._id}
                >
                  {cancellingId === effect._id ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <Trash2 size={16} />
                  )}
                </Button>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('common.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ManagerUserEffectsModal;
export type { ManagerUserEffectsModalProps };
